import { PIN_LENGTH } from './constants.js';
import { ProtocolError } from './errors.js';
import type { FileMeta } from './transfer.js';
import { parseFileMeta } from './transfer.js';

export type SessionDescription = Readonly<{
  type: 'offer' | 'answer';
  sdp: string;
}>;

export type IceCandidate = Readonly<{
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
}>;

export type SignalPayload =
  | Readonly<{ kind: 'description'; description: SessionDescription }>
  | Readonly<{ kind: 'candidate'; candidate: IceCandidate }>;

/** A file a customer wants to send to a permanent inbox, awaiting the owner's decision. */
export type InboxRequest = Readonly<{ roomId: string; file: FileMeta }>;

export type ClientMessage =
  | Readonly<{ type: 'create' }>
  | Readonly<{ type: 'join'; roomId: string }>
  | Readonly<{ type: 'join-by-pin'; pin: string }>
  | Readonly<{ type: 'signal'; payload: SignalPayload }>
  /** Sender side of a permanent inbox: opens a room addressed to the inbox with the file announced up front. */
  | Readonly<{ type: 'create-for-inbox'; inbox: string; file: FileMeta }>
  /** Owner side: proves ownership of the inbox with its secret key and starts receiving requests. */
  | Readonly<{ type: 'inbox-auth'; inbox: string; key: string }>
  | Readonly<{ type: 'inbox-accept'; roomId: string }>
  | Readonly<{ type: 'inbox-decline'; roomId: string }>;

export type SignalingErrorCode =
  | 'room-not-found'
  | 'room-full'
  | 'already-in-room'
  | 'not-in-room'
  | 'no-peer'
  | 'bad-message'
  | 'inbox-not-found'
  | 'inbox-unauthorized'
  | 'not-accepted'
  | 'not-inbox-owner';

export type ServerMessage =
  | Readonly<{ type: 'created'; roomId: string; pin: string }>
  | Readonly<{ type: 'joined'; roomId: string }>
  | Readonly<{ type: 'peer-joined' }>
  | Readonly<{ type: 'peer-left' }>
  | Readonly<{ type: 'signal'; payload: SignalPayload }>
  | Readonly<{ type: 'error'; code: SignalingErrorCode; message: string }>
  | Readonly<{ type: 'inbox-room-created'; roomId: string; ownerOnline: boolean }>
  | Readonly<{ type: 'inbox-ready'; inbox: string; pending: readonly InboxRequest[] }>
  | Readonly<{ type: 'inbox-request'; request: InboxRequest }>
  | Readonly<{ type: 'inbox-request-cancelled'; roomId: string }>
  | Readonly<{ type: 'inbox-accepted'; roomId: string }>
  | Readonly<{ type: 'inbox-declined' }>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJson = (raw: string): Record<string, unknown> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new ProtocolError(`Message is not valid JSON: ${String(error)}`);
  }
  if (!isRecord(parsed)) {
    throw new ProtocolError('Message must be a JSON object');
  }
  return parsed;
};

const parseDescription = (value: unknown): SessionDescription => {
  if (!isRecord(value)) {
    throw new ProtocolError('Session description must be an object');
  }
  const { type, sdp } = value;
  if (type !== 'offer' && type !== 'answer') {
    throw new ProtocolError(`Session description type must be offer or answer, got ${String(type)}`);
  }
  if (typeof sdp !== 'string' || sdp.length === 0) {
    throw new ProtocolError('Session description sdp must be a non-empty string');
  }
  return { type, sdp };
};

const parseCandidate = (value: unknown): IceCandidate => {
  if (!isRecord(value)) {
    throw new ProtocolError('ICE candidate must be an object');
  }
  const { candidate, sdpMid, sdpMLineIndex } = value;
  if (typeof candidate !== 'string') {
    throw new ProtocolError('ICE candidate string is missing');
  }
  if (sdpMid !== null && typeof sdpMid !== 'string') {
    throw new ProtocolError('ICE candidate sdpMid must be a string or null');
  }
  if (sdpMLineIndex !== null && typeof sdpMLineIndex !== 'number') {
    throw new ProtocolError('ICE candidate sdpMLineIndex must be a number or null');
  }
  return { candidate, sdpMid, sdpMLineIndex };
};

export const parseSignalPayload = (value: unknown): SignalPayload => {
  if (!isRecord(value)) {
    throw new ProtocolError('Signal payload must be an object');
  }
  switch (value['kind']) {
    case 'description':
      return { kind: 'description', description: parseDescription(value['description']) };
    case 'candidate':
      return { kind: 'candidate', candidate: parseCandidate(value['candidate']) };
    default:
      throw new ProtocolError(`Unknown signal payload kind: ${String(value['kind'])}`);
  }
};

const parseRoomId = (value: unknown): string => {
  if (typeof value !== 'string' || !/^[a-z0-9]{4,32}$/.test(value)) {
    throw new ProtocolError('roomId must be a short lowercase alphanumeric string');
  }
  return value;
};

const PIN_PATTERN = new RegExp(`^[0-9]{${PIN_LENGTH}}$`);

export const isValidPin = (value: string): boolean => PIN_PATTERN.test(value);

export const parsePin = (value: unknown): string => {
  if (typeof value !== 'string' || !isValidPin(value)) {
    throw new ProtocolError(`pin must be exactly ${PIN_LENGTH} digits`);
  }
  return value;
};

/** Inbox keys are base64url; the length bound keeps hostile payloads small. */
export const parseInboxKey = (value: unknown): string => {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{16,128}$/.test(value)) {
    throw new ProtocolError('key must be a base64url string');
  }
  return value;
};

const parseInboxRequest = (value: unknown): InboxRequest => {
  if (!isRecord(value)) {
    throw new ProtocolError('Inbox request must be an object');
  }
  return { roomId: parseRoomId(value['roomId']), file: parseFileMeta(value['file']) };
};

const parseInboxRequests = (value: unknown): InboxRequest[] => {
  if (!Array.isArray(value)) {
    throw new ProtocolError('pending must be an array');
  }
  return value.map(parseInboxRequest);
};

const parseBoolean = (value: unknown, field: string): boolean => {
  if (typeof value !== 'boolean') {
    throw new ProtocolError(`${field} must be a boolean`);
  }
  return value;
};

export const parseClientMessage = (raw: string): ClientMessage => {
  const message = parseJson(raw);
  switch (message['type']) {
    case 'create':
      return { type: 'create' };
    case 'join':
      return { type: 'join', roomId: parseRoomId(message['roomId']) };
    case 'join-by-pin':
      return { type: 'join-by-pin', pin: parsePin(message['pin']) };
    case 'signal':
      return { type: 'signal', payload: parseSignalPayload(message['payload']) };
    case 'create-for-inbox':
      return { type: 'create-for-inbox', inbox: parsePin(message['inbox']), file: parseFileMeta(message['file']) };
    case 'inbox-auth':
      return { type: 'inbox-auth', inbox: parsePin(message['inbox']), key: parseInboxKey(message['key']) };
    case 'inbox-accept':
      return { type: 'inbox-accept', roomId: parseRoomId(message['roomId']) };
    case 'inbox-decline':
      return { type: 'inbox-decline', roomId: parseRoomId(message['roomId']) };
    default:
      throw new ProtocolError(`Unknown client message type: ${String(message['type'])}`);
  }
};

const ERROR_CODES: ReadonlySet<string> = new Set<SignalingErrorCode>([
  'room-not-found',
  'room-full',
  'already-in-room',
  'not-in-room',
  'no-peer',
  'bad-message',
  'inbox-not-found',
  'inbox-unauthorized',
  'not-accepted',
  'not-inbox-owner',
]);

const parseErrorCode = (value: unknown): SignalingErrorCode => {
  if (typeof value !== 'string' || !ERROR_CODES.has(value)) {
    throw new ProtocolError(`Unknown error code: ${String(value)}`);
  }
  return value as SignalingErrorCode;
};

export const parseServerMessage = (raw: string): ServerMessage => {
  const message = parseJson(raw);
  switch (message['type']) {
    case 'created':
      return { type: 'created', roomId: parseRoomId(message['roomId']), pin: parsePin(message['pin']) };
    case 'joined':
      return { type: 'joined', roomId: parseRoomId(message['roomId']) };
    case 'peer-joined':
      return { type: 'peer-joined' };
    case 'peer-left':
      return { type: 'peer-left' };
    case 'signal':
      return { type: 'signal', payload: parseSignalPayload(message['payload']) };
    case 'inbox-room-created':
      return {
        type: 'inbox-room-created',
        roomId: parseRoomId(message['roomId']),
        ownerOnline: parseBoolean(message['ownerOnline'], 'ownerOnline'),
      };
    case 'inbox-ready':
      return { type: 'inbox-ready', inbox: parsePin(message['inbox']), pending: parseInboxRequests(message['pending']) };
    case 'inbox-request':
      return { type: 'inbox-request', request: parseInboxRequest(message['request']) };
    case 'inbox-request-cancelled':
      return { type: 'inbox-request-cancelled', roomId: parseRoomId(message['roomId']) };
    case 'inbox-accepted':
      return { type: 'inbox-accepted', roomId: parseRoomId(message['roomId']) };
    case 'inbox-declined':
      return { type: 'inbox-declined' };
    case 'error': {
      const text = message['message'];
      return {
        type: 'error',
        code: parseErrorCode(message['code']),
        message: typeof text === 'string' ? text : 'Unknown signaling error',
      };
    }
    default:
      throw new ProtocolError(`Unknown server message type: ${String(message['type'])}`);
  }
};
